import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { PLAYS_ID, STRATEGY_METRIC_TYPE } from './constants';

export type PlaysRegenerationJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed';

export interface PlaysRegenerationJob {
  id: number;
  status: PlaysRegenerationJobStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

// Base API for all strategy endpoints
export const playsApi = createApi({
  reducerPath: 'playsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [PLAYS_ID, STRATEGY_METRIC_TYPE],
  endpoints: (builder) => ({
    // Define endpoints here as needed
    getPlays: builder.query<{ results: any[] }, void>({
      query: () => ({
        url: 'v1/strategies/templates/plays/',
        method: 'GET',
      }),
    }),
    getPlaysDetails: builder.query<Record<string, any>, { playId: string }>({
      query: ({ playId }) => ({
        url: `v1/strategies/templates/plays/${playId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { playId }) => [
        { type: PLAYS_ID, id: playId },
      ],
    }),
    regeneratePlays: builder.mutation<PlaysRegenerationJob, void>({
      query: () => ({
        url: 'v1/accounts/plays/regenerate/',
        method: 'POST',
      }),
    }),
    /**
     * List plays regeneration jobs for the current user.
     * Used to check if there's an active job on page load.
     */
    getPlaysRegenerationJobs: builder.query<
      { count: number; results: PlaysRegenerationJob[] },
      void
    >({
      query: () => 'v1/accounts/plays/regenerate/',
    }),
    /**
     * Poll a plays regeneration job by ID.
     * Used to check status after triggering regeneration.
     */
    getPlaysRegenerationJob: builder.query<PlaysRegenerationJob, number>({
      query: (jobId) => `v1/accounts/plays/regenerate/${jobId}/`,
    }),
  }),
});

export const {
  useGetPlaysQuery,
  useGetPlaysDetailsQuery,
  useRegeneratePlaysMutation,
  useGetPlaysRegenerationJobsQuery,
  useGetPlaysRegenerationJobQuery,
} = playsApi;
